import {
  Badge,
  Spinner,
  TabItem,
  Tabs as TabsComponent,
} from '@dixa/lib-shared-habla';
import type { AdvancedSearchResultScope } from 'lib-shared-types-api';
import { ReactElement, useMemo } from 'react';

import { searchApi } from 'apis/search';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { maxResultsCount } from '../../utils/results/maxResultsCount';

import styles from './searchTabs.module.scss';

type SearchTabsProps = {
  onChange: (newTab: AdvancedSearchResultScope) => void;
};

/**
 * Represents a component that is used to render the tabs that separate search
 * results.
 */
export const SearchTabs = ({
  onChange: onTabChange,
}: SearchTabsProps): JSX.Element => {
  const { params } = useAppSelector((state) => state.search);
  const { scope: activeTab } = useAppPathParams('/search/:scope');

  const { count, isFetching } =
    searchApi.endpoints.readAdvancedSearchResults.useQueryState(
      {
        params,
      },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          count: Math.min(
            data?.data.results.reduce(
              (acc, result) => (acc += result.nb_hits),
              0,
            ) ?? 0,
            maxResultsCount,
          ),
        }),
      },
    );

  const tabs = useMemo(
    (): TabItem[] =>
      Object.keys(tabsRecord).map((type): TabItem => {
        const { label } = tabsRecord[type as AdvancedSearchResultScope];
        const showCount =
          activeTab === type &&
          (!!params.query || params.filters.conditions.length > 0);

        const tabElement: ReactElement = (
          <>
            <span>{label}</span>

            {showCount && (
              <Badge
                variant="secondary"
                size="small"
                className={styles.tabBadge}
              >
                {isFetching ? (
                  <Spinner size={12} />
                ) : (
                  count
                    .toLocaleString(navigator.language)
                    .concat(count >= maxResultsCount ? '+' : '')
                )}
              </Badge>
            )}
          </>
        );

        return { id: type, label: tabElement };
      }),
    [
      activeTab,
      count,
      isFetching,
      params.filters.conditions.length,
      params.query,
    ],
  );

  return (
    <div className={styles.tabsContainer}>
      <TabsComponent
        tabs={tabs}
        selectedId={activeTab}
        onChange={(tab) => onTabChange(tab as AdvancedSearchResultScope)}
        tabClassName={styles.tab}
        tabSelectedClassName={styles.tabSelected}
      />
    </div>
  );
};

const tabsRecord: Record<AdvancedSearchResultScope, { label: string }> = {
  conversations: { label: 'Conversations' },
  notes: { label: 'Notes' },
  contacts: { label: 'Contacts' },
};
